import { Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, LoaderCircle, Lock, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { puedeRegistrarEnObra } from "@/app/guards";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { type CampoFiltro, FiltrosBar, type RangoFechas } from "@/components/ui/filtros-bar";
import { Modal } from "@/components/ui/modal";
import { useMe } from "@/lib/auth";
import { useEquipos } from "@/lib/equipos";
import { AVISO_FILTRADO_LOCAL, mensajeErrorLista, useFiltrosURL } from "@/lib/filtros";
import { CLAVES_LOTOS } from "@/lib/filtros/lotos";
import {
  type EstadoLoto,
  type LotoInput,
  type LotoListItem,
  useCreateLoto,
  useLotos,
} from "@/lib/lotos";
import { type Procedimiento, useProcedimientos } from "@/lib/procedimientos";
import { AvisoDocumentacionAcreditativa } from "./AvisoDocumentacionAcreditativa";
import { ExportLotos } from "./ExportLotos";
import { ESTADO_LOTO, EstadoLotoBadge } from "./estado";
import { LotoForm, type LotoFormValues } from "./LotoForm";

// Los dos rangos de fechas del índice. Cada par lo comparten sus dos extremos, que es como
// la barra sabe cuál soltar si el usuario invierte el rango (#247, ronda 2).
const RANGO_APERTURA: RangoFechas = { desde: "desde", hasta: "hasta" };
const RANGO_CIERRE: RangoFechas = { desde: "cierre_desde", hasta: "cierre_hasta" };

// Catálogo vacío con identidad ESTABLE para el hueco en el que la consulta aún está en
// vuelo (#293 D6): un `?? []` en el JSX inventaba un array nuevo en cada render y el
// formulario lo leía como un catálogo distinto.
const SIN_PROCEDIMIENTOS: Procedimiento[] = [];

// formatFecha da un instante ISO en formato local es-ES (día y hora), para la columna de
// apertura. "—" si el instante no es válido.
function formatFecha(iso: string): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) {
    return "—";
  }
  return fecha.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// NombreOGuion pinta el nombre de un actor del LOTO (#250) o un guion cuando no lo hay: el
// LOTO sigue abierto (D2) o su autor ya no existe (D6, el servidor manda cadena vacía). Es el
// mismo guion atenuado con el que el índice ya representa una celda sin valor.
function NombreOGuion({ nombre }: { nombre: string }) {
  if (nombre.trim() === "") {
    return <span className="text-slate-400">—</span>;
  }
  return <span className="text-slate-500">{nombre}</span>;
}

// toInput normaliza los valores del formulario al payload del API: número vacío ⇒ se omite
// (el servidor lo autogenera); descripción/observaciones vacías ⇒ null; procedimiento sin
// elegir ⇒ null (#236: el LOTO se abre igual que antes, sin procedimiento).
function toInput(values: LotoFormValues): LotoInput {
  const numero = values.numero_loto.trim();
  const descripcion = values.descripcion.trim();
  const observaciones = values.observaciones.trim();
  const procedimiento = values.id_procedimiento.trim();
  return {
    id_equipo: values.id_equipo,
    id_procedimiento: procedimiento === "" ? null : Number(procedimiento),
    numero_loto: numero === "" ? undefined : numero,
    descripcion: descripcion === "" ? null : descripcion,
    observaciones: observaciones === "" ? null : observaciones,
  };
}

/**
 * LotosPage es el índice de LOTOs: los aislamientos en curso y el histórico de cerrados,
 * del más reciente al más antiguo. La tabla usa el DataTable de react-table (#34) SIN
 * buscador en memoria (`sinBuscador`): el texto libre y el filtro por estado son del SERVIDOR y
 * viven en la FiltrosBar (#247), y la tabla solo ordena y pagina, así que sus `accessorFn`
 * alimentan la celda, la ordenación o ambas, nunca una búsqueda (#346).
 * Cada fila abre su ficha (/lotos/$id);
 * el número lleva un indicador rojo de candados activos cuando el equipo está bloqueado.
 * «Nuevo LOTO» solo aparece a quien puede registrar operación en la obra (regla
 * puede_registrar de la obra); el backend es el guardián real.
 */
export function LotosPage() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const obraId = useObraId();
  const puedeRegistrar = me != null && puedeRegistrarEnObra(me, obraId);

  // Los equipos alimentan el desplegable del formulario (limitado a loteables y activos),
  // las opciones de los filtros por equipo y área y —sin conexión— la resolución del filtro
  // por área, que el servidor hace por id_area y la fila del índice no publica (#247).
  const { data: equipos } = useEquipos(obraId);
  const equiposParaArea = useMemo(
    () => (equipos ?? []).map((e) => ({ id: e.id, id_area: e.id_area })),
    [equipos],
  );
  // Los procedimientos alimentan el selector del alta (#236): el formulario se queda con los
  // ACTIVOS del equipo elegido. Están en la allowlist de la caché offline, así que sin red el
  // selector se puebla con lo último cacheado.
  const { data: procedimientos } = useProcedimientos(obraId);
  // Los filtros viven en la URL (#247): compartible, recargable y con el servidor filtrando.
  const { filtros, setFiltros } = useFiltrosURL(CLAVES_LOTOS);
  const {
    data: lotos,
    base: lotosBase,
    isPending,
    isError,
    baseVacia,
    avisoSinConexion,
    codigoError,
    camposError,
  } = useLotos(obraId, filtros, equiposParaArea);
  const filas = lotos ?? [];
  const crear = useCreateLoto(obraId);

  const [abrirForm, setAbrirForm] = useState(false);

  const equiposLoteables = useMemo(
    () => (equipos ?? []).filter((e) => e.loteable && e.activo),
    [equipos],
  );

  const columns = useMemo<ColumnDef<LotoListItem, unknown>[]>(
    () => [
      {
        accessorKey: "numero_loto",
        header: "Nº LOTO",
        cell: ({ row }) => (
          <Link
            to="/obras/$obraId/lotos/$id"
            params={{ obraId: String(obraId), id: String(row.original.id) }}
            className="font-medium text-primary-700 tabular-nums hover:underline"
          >
            {row.original.numero_loto}
          </Link>
        ),
      },
      {
        id: "equipo",
        accessorFn: (l) => `${l.equipo} ${l.codigo_equipo} ${l.area}`,
        header: "Equipo",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium text-slate-900">{row.original.equipo}</span>
            <span className="text-slate-500 text-sm">
              {row.original.codigo_equipo} · {row.original.area}
            </span>
          </div>
        ),
      },
      {
        id: "estado",
        // El accessor es el valor del enum (no la etiqueta) y su ÚNICO cliente es la
        // ordenación de la columna: la celda pinta su badge desde `row.original` y la
        // exportación es del SERVIDOR (#295), que nunca ve el modelo de la tabla (#346).
        accessorFn: (l) => l.estado,
        header: "Estado",
        cell: ({ row }) => <EstadoLotoBadge estado={row.original.estado} />,
      },
      {
        accessorKey: "bloqueos_activos",
        header: "Candados",
        cell: ({ row }) =>
          row.original.bloqueos_activos > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 font-semibold text-red-700 text-xs">
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              {row.original.bloqueos_activos} activos
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          ),
      },
      {
        accessorKey: "fecha_apertura",
        header: "Apertura",
        cell: ({ row }) => (
          <span className="text-slate-500">{formatFecha(row.original.fecha_apertura)}</span>
        ),
        meta: { className: "hidden sm:table-cell" },
      },
      // Quién abrió el LOTO y quién lo cerró (#250), cada uno junto a su fecha. El nombre
      // vacío es un dato legítimo —su cuenta se borró (D6) o el LOTO sigue abierto (D2)—:
      // se pinta «—», el mismo guion que ya usan las demás celdas sin valor.
      {
        accessorKey: "creado_por_nombre",
        header: "Abierto por",
        cell: ({ row }) => <NombreOGuion nombre={row.original.creado_por_nombre} />,
        meta: { className: "hidden sm:table-cell" },
      },
      {
        accessorKey: "cerrado_por_nombre",
        header: "Cerrado por",
        cell: ({ row }) => <NombreOGuion nombre={row.original.cerrado_por_nombre} />,
        meta: { className: "hidden sm:table-cell" },
      },
    ],
    [obraId],
  );

  // Las opciones de «Abierto por» salen de los creadores presentes en la lista BASE (#250,
  // D3): no hace falta endpoint nuevo, es lo que la función pura puede replicar sin red, y
  // no tiene sentido ofrecer filtrar por quien no ha abierto ningún LOTO. Se indexan por id
  // —que es lo que se emite— y los LOTOs sin autor (D6) no aportan opción.
  const opcionesCreador = useMemo(() => {
    const vistos = new Map<number, string>();
    for (const l of lotosBase ?? []) {
      if (l.creado_por != null && l.creado_por_nombre !== "") {
        vistos.set(l.creado_por, l.creado_por_nombre);
      }
    }
    return [...vistos.entries()]
      .map(([id, etiqueta]) => ({ valor: String(id), etiqueta }))
      .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta));
  }, [lotosBase]);

  // Campos de la barra de filtros. Los valores son los del API (ids, enums y días naturales
  // YYYY-MM-DD), no las etiquetas: `creado_por` viaja como ID, que es lo que casa el
  // servidor y lo que replica la función pura sin red (#250, D4).
  const campos = useMemo<CampoFiltro[]>(
    () => [
      { tipo: "texto", nombre: "q", etiqueta: "Buscar", placeholder: "Buscar LOTOs…" },
      {
        tipo: "select",
        nombre: "estado",
        etiqueta: "Estado",
        // Orden estable por el ciclo de vida (#241), no por aparición en los datos.
        opciones: (["ABIERTO", "CERRADO"] as EstadoLoto[]).map((e) => ({
          valor: e,
          etiqueta: ESTADO_LOTO[e].label,
        })),
      },
      {
        tipo: "select",
        nombre: "equipo",
        etiqueta: "Equipo",
        opciones: (equipos ?? []).map((e) => ({
          valor: String(e.id),
          etiqueta: `${e.codigo_equipo} · ${e.nombre_equipo}`,
        })),
      },
      {
        tipo: "select",
        nombre: "area",
        etiqueta: "Área",
        opciones: Array.from(
          new Map((equipos ?? []).map((e) => [String(e.id_area), e.nombre_area])).entries(),
        )
          .sort((a, b) => a[1].localeCompare(b[1]))
          .map(([valor, etiqueta]) => ({ valor, etiqueta })),
      },
      { tipo: "select", nombre: "creado_por", etiqueta: "Abierto por", opciones: opcionesCreador },
      // Los dos extremos de cada rango se declaran emparejados: así la barra impide
      // construir un rango invertido, que el servidor rechazaría con un 400.
      { tipo: "fecha", nombre: "desde", etiqueta: "Abierto desde", rango: RANGO_APERTURA },
      { tipo: "fecha", nombre: "hasta", etiqueta: "Abierto hasta", rango: RANGO_APERTURA },
      { tipo: "fecha", nombre: "cierre_desde", etiqueta: "Cerrado desde", rango: RANGO_CIERRE },
      { tipo: "fecha", nombre: "cierre_hasta", etiqueta: "Cerrado hasta", rango: RANGO_CIERRE },
    ],
    [equipos, opcionesCreador],
  );

  async function onSubmitForm(values: LotoFormValues) {
    const loto = await crear.mutateAsync(toInput(values));
    setAbrirForm(false);
    // Tras abrir el LOTO se salta a su ficha para empezar a colocar candados.
    await navigate({
      to: "/obras/$obraId/lotos/$id",
      params: { obraId: String(obraId), id: String(loto.id) },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">LOTOs</h1>
          <p className="text-base text-slate-500">Aislamientos en curso e histórico de bloqueos.</p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {/* La exportación (#214) hereda los filtros de la URL, TODOS y por una sola vía
              (#319): `filtrosURL` es la fuente y el rango solo siembra el popover. */}
          <ExportLotos
            obraId={obraId}
            desdeInicial={filtros.desde}
            hastaInicial={filtros.hasta}
            filtrosURL={filtros}
          />
          {puedeRegistrar ? (
            <Button onClick={() => setAbrirForm(true)}>
              <Plus className="h-5 w-5" aria-hidden="true" />
              Nuevo LOTO
            </Button>
          ) : null}
        </div>
      </div>

      {/* #435 (R12): el motivo por el que no aparece «Nuevo LOTO» si lo que falta es la
          documentación acreditativa. */}
      <AvisoDocumentacionAcreditativa me={me} obraId={obraId} />

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {mensajeErrorLista(
              { codigoError, camposError },
              "No se pudieron cargar los LOTOs. Inténtalo de nuevo.",
            )}
          </span>
        </Alert>
      ) : null}

      {isPending ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando LOTOs…
        </div>
      ) : null}

      {!isPending && !isError && baseVacia ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
          Aún no hay LOTOs.{puedeRegistrar ? " Abre el primero con «Nuevo LOTO»." : ""}
        </div>
      ) : null}

      {!baseVacia ? (
        <>
          <FiltrosBar
            campos={campos}
            valor={filtros}
            onChange={setFiltros}
            aviso={avisoSinConexion ? AVISO_FILTRADO_LOCAL : undefined}
          />
          {!isPending ? (
            <DataTable
              columns={columns}
              data={filas}
              sinBuscador
              onRowClick={(loto) =>
                navigate({
                  to: "/obras/$obraId/lotos/$id",
                  params: { obraId: String(obraId), id: String(loto.id) },
                })
              }
            />
          ) : null}
        </>
      ) : null}

      {/* Apertura de un LOTO */}
      <Modal open={abrirForm} onClose={() => setAbrirForm(false)} title="Nuevo LOTO">
        <LotoForm
          defaultValues={{
            id_equipo: 0,
            id_procedimiento: "",
            numero_loto: "",
            descripcion: "",
            observaciones: "",
          }}
          equipos={equiposLoteables}
          procedimientos={procedimientos ?? SIN_PROCEDIMIENTOS}
          pending={crear.isPending}
          onSubmit={onSubmitForm}
          onCancel={() => setAbrirForm(false)}
        />
      </Modal>
    </div>
  );
}
